import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import { useScrollContainer } from '../../context/ScrollContainerContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';

export default function ImageParallax() {
  const { language, t } = useI18n();
  const sectionRef = useRef(null);
  const scrollContainerRef = useScrollContainer();
  const scrollOptions = {
    target: sectionRef,
    offset: ['start end', 'end start'],
  };
  if (scrollContainerRef) {
    scrollOptions.container = scrollContainerRef;
  }

  const { scrollYProgress } = useScroll(scrollOptions);
  const imageY = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);

  return (
    <section key={language} ref={sectionRef} className="relative overflow-hidden py-16 lg:py-24">
      <div className="pointer-events-none absolute inset-0">
        <ImageOnlySkeletonLoader
          src="/parallax/1.webp"
          mode="native"
          alt=""
          containerClassName="h-full w-full"
          imgClassName="h-full w-full scale-110 object-cover blur-md"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/65" />
      </div>

      <div className="container mx-auto px-6 lg:px-12">
        <div className="relative h-[380px] overflow-hidden rounded-[2rem] lg:h-[520px]">
          <motion.div
            style={{ y: imageY }}
            className="absolute inset-x-0 -top-[10%] h-[140%] w-full transform-gpu will-change-transform"
          >
            <ImageOnlySkeletonLoader
              src="/parallax/1.webp"
              mode="native"
              alt="Industrial logistics"
              containerClassName="h-full w-full"
              imgClassName="h-full w-full object-cover object-[50%_42%]"
            />
          </motion.div>

          <motion.div
            style={{ opacity: 0.9 }}
            className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-full md:w-[80%] lg:w-[70%] bg-gradient-to-r from-black via-black/75 to-transparent"
          />

          <div className="relative z-10 flex h-full max-w-2xl flex-col justify-end p-8 lg:p-12">
            <AnimatedEyebrow
              key={language}
              className="mb-6"
              borderClassName="border-[var(--dash-other)]"
              surfaceClassName="bg-white/10"
              dotClassName="bg-[var(--dash-other)] shadow-[0_0_8px_var(--dash-other)]"
            >
              {t('home.imageParallax.eyebrow', 'Field View')}
            </AnimatedEyebrow>
            <h2 className="text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
              <span className="block">{t('home.imageParallax.titleLine1')}</span>
              <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
                {t('home.imageParallax.titleLine2')}
              </span>
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-200 md:text-base">
              {t(
                'home.imageParallax.description',
                'High-performance handling systems engineered for demanding environments and long-term reliability.',
              )}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
